// QR encode (qrcode) + scan (native BarcodeDetector when available, else jsQR).

import QRCode from 'qrcode';
import jsQR from 'jsqr';

const APP_URL = (import.meta.env?.VITE_APP_URL as string | undefined) ?? 'https://app.seabearing.com/';
const JOIN_PATH = '/j';
const PENDING_KEY = 'seabearing:join:pending';
const REDIRECT_KEY = 'seabearing:join:url';
const PENDING_TTL_MS = 24 * 3600 * 1000;

function appOrigin(): string {
  try {
    return new URL(APP_URL).origin;
  } catch {
    return 'https://app.seabearing.com';
  }
}

function isAllowedJoinOrigin(origin: string): boolean {
  if (origin === appOrigin()) return true;
  try {
    return typeof window !== 'undefined' && origin === window.location.origin;
  } catch {
    return false;
  }
}

function encodeFragmentPayload(payload: string): string {
  // QR payloads are ASCII. Leave the QR-dense characters alone, but escape characters that would
  // change fragment parsing; crew pairing codes can contain literal '%'.
  return payload.replace(/%/g, '%25').replace(/#/g, '%23');
}

function decodeFragmentPayload(hash: string): string | null {
  if (!hash || hash === '#') return null;
  try {
    const payload = decodeURIComponent(hash.slice(1)).trim();
    return payload || null;
  } catch {
    return null;
  }
}

/** The app/web URL a phone camera should open for a Seabearing QR payload. The payload lives in the
 *  fragment so it is never sent to the web server. */
export function seabearingJoinUrl(payload: string): string {
  const u = new URL(JOIN_PATH, appOrigin());
  u.hash = encodeFragmentPayload(payload);
  return u.toString();
}

/** Pull the Seabearing QR payload out of an app/web URL, or null if it is not one. */
export function parseSeabearingJoinUrl(text: string): string | null {
  const t = (text || '').trim();
  if (!t) return null;
  let url: URL;
  try {
    url = new URL(t);
  } catch {
    return null;
  }
  if (!isAllowedJoinOrigin(url.origin)) return null;
  if (url.pathname !== JOIN_PATH && url.pathname !== `${JOIN_PATH}/` && url.pathname !== '/') return null;
  return decodeFragmentPayload(url.hash);
}

export function stashSeabearingJoinPayload(payload: string): void {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify({ payload, at: Date.now() })); } catch { /* private mode */ }
}

export function pendingSeabearingJoinPayload(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { payload?: unknown; at?: unknown };
    if (typeof p.payload !== 'string' || !p.payload || typeof p.at !== 'number' || Date.now() - p.at > PENDING_TTL_MS) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return p.payload;
  } catch {
    return null;
  }
}

export function clearPendingSeabearingJoinPayload(payload?: string): void {
  try {
    if (payload && pendingSeabearingJoinPayload() !== payload) return;
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

function takeRedirectedJoinUrl(): string | null {
  let href: string | null = null;
  for (const store of [sessionStorage, localStorage]) {
    try {
      href ??= store.getItem(REDIRECT_KEY);
      store.removeItem(REDIRECT_KEY);
    } catch {
      /* private mode */
    }
  }
  return href;
}

/** Capture a /j#... web fallback or native-start URL before the auth wall, then clean the address bar. */
export function captureSeabearingJoinUrl(): string | null {
  const href = takeRedirectedJoinUrl() ?? window.location.href;
  const payload = parseSeabearingJoinUrl(href);
  if (!payload) return null;
  stashSeabearingJoinPayload(payload);
  try {
    const here = new URL(window.location.href);
    if (here.hash || here.pathname === JOIN_PATH || here.pathname === `${JOIN_PATH}/`) {
      window.history.replaceState(window.history.state, '', '/' + here.search);
    }
  } catch {
    /* no history API */
  }
  return payload;
}

/** A QR code for `text` as an SVG data URL, drawn for a phone camera reading it off a screen:
 *  black on white, the standard 4-module quiet zone, and the strongest error correction (M at
 *  least) that does not make the code any bigger than M does — a little glare or a smudge on the
 *  screen is then still read, at no cost in module size. Sized by CSS (.sb-qr), crisp at any size. */
export async function makeQR(text: string): Promise<string> {
  const version = QRCode.create(text, { errorCorrectionLevel: 'M' }).version;
  let level: 'M' | 'Q' | 'H' = 'M';
  for (const l of ['Q', 'H'] as const) {
    try {
      if (QRCode.create(text, { errorCorrectionLevel: l }).version === version) level = l;
    } catch { /* too long at this level */ }
  }
  const svg = await QRCode.toString(text, { type: 'svg', errorCorrectionLevel: level, version, margin: 4, color: { dark: '#000000', light: '#ffffff' } });
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

const SCAN_INTERVAL_MS = 150;

type Detector = { detect: (src: CanvasImageSource) => Promise<{ rawValue: string }[]> };
let detector: Detector | null | undefined;

/** The native QR detector, or null where there is none (Safari, Firefox) or it failed once. */
function getDetector(): Detector | null {
  if (detector === undefined) {
    try {
      const BD = (globalThis as { BarcodeDetector?: new (o: { formats: string[] }) => Detector }).BarcodeDetector;
      detector = BD ? new BD({ formats: ['qr_code'] }) : null;
    } catch {
      detector = null;
    }
  }
  return detector;
}

/** Decode a QR from the video's current frame, or null. The native detector reads the video
 *  directly; jsQR needs the frame's pixels, drawn into `canvas` (resized only when the frame size
 *  changes — every resize reallocates its backing store). Our codes are always dark on light, so
 *  jsQR does not also try the inverted image. */
async function decodeFrame(video: HTMLVideoElement, canvas: HTMLCanvasElement): Promise<string | null> {
  if (video.readyState < 2 || !video.videoWidth) return null;
  const det = getDetector();
  if (det) {
    try {
      return (await det.detect(video))[0]?.rawValue || null;
    } catch {
      detector = null; // unsupported here after all: jsQR from now on
    }
  }
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no 2d canvas');
  ctx.drawImage(video, 0, 0);
  return jsQR(ctx.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: 'dontInvert' })?.data || null;
}

export interface QrScan {
  /** Tear the camera down. Idempotent; works while the camera is still opening. */
  stop(): void;
}

export interface QrScanOptions {
  /** Camera constraints. Default: the rear camera at its default size (plenty for short payloads;
   *  the dense crew SDP code asks for 1920×1080). */
  video?: MediaTrackConstraints;
  /** Each newly decoded payload (a code held steady fires once). The caller may stop() here. */
  onResult: (value: string) => void;
  /** The camera could not be opened, or the decoder failed: scanning has stopped — offer paste. */
  onError?: () => void;
  /** The camera is live; its track says which device was opened. */
  onStart?: (track: MediaStreamTrack) => void;
}

/** Open a camera into `video` and scan it for QR codes until stop(). Returns at once. */
export function scanQR(video: HTMLVideoElement, opts: QrScanOptions): QrScan {
  const canvas = document.createElement('canvas');
  let scanning = true;
  let stream: MediaStream | null = null;
  let last = '';
  const stop = (): void => {
    scanning = false;
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    if (video.srcObject) video.srcObject = null;
  };
  const fail = (): void => {
    if (!scanning) return; // stopped meanwhile: nothing to report
    stop();
    opts.onError?.();
  };
  const tick = async (): Promise<void> => {
    if (!scanning) return;
    let code: string | null;
    try {
      code = await decodeFrame(video, canvas);
    } catch {
      fail();
      return;
    }
    if (scanning && code && code !== last) {
      last = code;
      opts.onResult(code);
    }
    if (scanning) setTimeout(() => requestAnimationFrame(() => void tick()), SCAN_INTERVAL_MS);
  };
  void (async () => {
    let s: MediaStream;
    try {
      s = await navigator.mediaDevices.getUserMedia({ video: opts.video ?? { facingMode: 'environment' } });
    } catch {
      fail();
      return;
    }
    // stop() ran while getUserMedia awaited (screen closed, step changed): close the orphan at once
    if (!scanning) {
      s.getTracks().forEach((t) => t.stop());
      return;
    }
    stream = s;
    video.srcObject = s;
    await video.play().catch(() => {});
    if (!scanning) return;
    const track = s.getVideoTracks()[0];
    if (track) opts.onStart?.(track);
    void tick();
  })();
  return { stop };
}
